import React, { useState } from 'react'
import { apiPost } from '../../utils/api'

interface WritingAssistantProps {
  text: string
  onResult: (result: string) => void
  chatId: string
}

type Action = 'rephrase' | 'expand' | 'shorten' | 'grammar' | 'formal' | 'friendly'

export const WritingAssistant: React.FC<WritingAssistantProps> = ({ text, onResult, chatId }) => {
  const [loading, setLoading] = useState(false)
  const [activeAction, setActiveAction] = useState<Action | null>(null)

  const actions: { key: Action; label: string; icon: string }[] = [
    { key: 'rephrase', label: 'Rephrase', icon: '🔄' },
    { key: 'expand', label: 'Expand', icon: '📝' },
    { key: 'shorten', label: 'Shorten', icon: '✂️' },
    { key: 'grammar', label: 'Fix Grammar', icon: '✓' },
    { key: 'formal', label: 'More Formal', icon: '👔' },
    { key: 'friendly', label: 'More Friendly', icon: '😊' },
  ]

  const handleAction = async (action: Action) => {
    if (!text.trim() || loading) return
    setLoading(true)
    setActiveAction(action)
    try {
      const result = await apiPost<{ result: string }>(`/chats/${chatId}/writing-assist`, {
        text,
        action,
      })
      onResult(result.result)
    } catch (err) {
      console.error('Writing assistant error:', err)
    } finally {
      setLoading(false)
      setActiveAction(null)
    }
  }

  if (!text.trim()) return null

  return (
    <div className="flex items-center gap-1 px-4 pb-1">
      <span className="text-[10px] font-semibold text-txt-tertiary uppercase tracking-wider mr-1">AI:</span>
      {actions.map(a => (
        <button
          key={a.key}
          onClick={() => handleAction(a.key)}
          disabled={loading}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
            activeAction === a.key
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
              : 'text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover'
          } disabled:opacity-40`}
          title={a.label}
        >
          <span>{a.icon}</span>
          <span className="hidden sm:inline">{a.label}</span>
        </button>
      ))}
      {loading && (
        <span className="ml-1 text-[10px] text-purple-500 animate-pulse">Processing...</span>
      )}
    </div>
  )
}
